import { Component, OnInit } from '@angular/core';
import { ChartData, ChartOptions } from 'chart.js';
import { DiscrepancyReportService, DiscrepancyStatusDashboardDTO, ProductDiscrepancyDTO } from '../service/discrepancy-report.service';

@Component({
  selector: 'app-discrepancy-dashboard',
  templateUrl: './discrepancy-dashboard.component.html',
  styleUrls: ['./discrepancy-dashboard.component.css']
})
export class DiscrepancyDashboardComponent implements OnInit {
  // Loading state
  isLoading: boolean = false;
  
  // Date range selection
  selectedRange: string = 'month';
  startDate: string = '';
  endDate: string = '';

  // Status-based metrics
  pendingCash: number = 0;
  approvedCash: number = 0;
  rejectedCash: number = 0;
  
  pendingStock: number = 0;
  approvedStock: number = 0;
  rejectedStock: number = 0;
  
  pendingTotal: number = 0;
  approvedTotal: number = 0;
  rejectedTotal: number = 0;
  grandTotal: number = 0;

  // Chart for status-based timeline (pending/approved/rejected over time)
  statusTimelineChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: { size: 12, weight: 500 }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${context.parsed.y.toLocaleString()} MAD`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: {
          color: '#6B7280',
          font: { size: 11 },
          callback: (value: any) => `${Number(value).toLocaleString()} MAD`
        }
      },
      x: {
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: { color: '#6B7280', font: { size: 11 } }
      }
    }
  };
  
  statusTimelineChartData: ChartData<'line'> = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'En Attente',
        borderColor: 'rgb(234, 179, 8)',
        backgroundColor: 'rgba(234, 179, 8, 0.1)',
        pointBackgroundColor: 'rgb(234, 179, 8)',
        pointBorderColor: 'rgb(234, 179, 8)',
        fill: true,
        tension: 0.4
      },
      {
        data: [],
        label: 'Approuvé',
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        pointBackgroundColor: 'rgb(34, 197, 94)',
        pointBorderColor: 'rgb(34, 197, 94)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  // Chart for cash vs stock by status
  statusBreakdownChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: { size: 12, weight: 500 }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${context.parsed.y.toLocaleString()} MAD`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: {
          color: '#6B7280',
          font: { size: 11 },
          callback: (value: any) => `${Number(value).toLocaleString()} MAD`
        }
      },
      x: {
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: { color: '#6B7280', font: { size: 11 } }
      }
    }
  };

  statusBreakdownChartData: ChartData<'bar'> = {
    labels: ['En Attente', 'Approuvé', 'Rejeté'],
    datasets: [
      {
        label: 'Caisse',
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        data: [0, 0, 0]
      },
      {
        label: 'Stock',
        backgroundColor: 'rgba(234, 179, 8, 0.8)',
        data: [0, 0, 0]
      }
    ]
  };

  // New charts: Cash, Stock, Combined evolution with totals
  cashEvolutionOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { usePointStyle: true, padding: 20, font: { size: 12, weight: 500 } }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: { label: (c: any) => `${c.dataset.label}: ${c.parsed.y.toLocaleString()} MAD` }
      }
    },
    scales: {
      y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { color: '#6B7280', font: { size: 11 }, callback: (v: any) => `${Number(v).toLocaleString()} MAD` } },
      x: { grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { color: '#6B7280', font: { size: 11 } } }
    }
  };
  stockEvolutionOptions: ChartOptions = { ...this.cashEvolutionOptions };
  combinedEvolutionOptions: ChartOptions = { ...this.cashEvolutionOptions };

  cashEvolutionData: ChartData<'line'> = { labels: [], datasets: [] };
  stockEvolutionData: ChartData<'line'> = { labels: [], datasets: [] };
  combinedEvolutionData: ChartData<'line'> = { labels: [], datasets: [] };

  // Discrepancy by product chart (simple top products by approved value)
  productDiscrepancyOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: { label: (c: any) => `${c.label}: ${c.parsed.y?.toLocaleString()} unités` }
      }
    },
    scales: {
      y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { color: '#6B7280', font: { size: 11 }, callback: (v: any) => `${Number(v).toLocaleString()} unités` } },
      x: { grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { display: false } }
    }
  };
  productDiscrepancyData: ChartData<'bar'> = { labels: [], datasets: [{ label: 'Top 10 - Pertes de Stock', data: [], backgroundColor: 'rgba(99, 102, 241, 0.8)' }] };

  // Top products data
  topProducts: ProductDiscrepancyDTO[] = [];

  constructor(private discrepancyService: DiscrepancyReportService) {}

  ngOnInit(): void {
    const range = this.computeRange(this.selectedRange);
    this.startDate = range.start;
    this.endDate = range.end;
    this.loadDashboard();
  }

  onRangeChange(range: string): void {
    this.selectedRange = range;
    const computed = this.computeRange(range);
    this.startDate = computed.start;
    this.endDate = computed.end;
    this.loadDashboard();
  }

  refresh(): void {
    this.loadDashboard();
  }

  onCustomDateChange(): void {
    this.selectedRange = ''; // Clear quick selection when using custom dates
    this.loadDashboard();
  }

  private computeRange(range: string): { start: string; end: string } {
    const today = new Date();
    let start: Date;
    let end: Date;

    switch (range) {
      case 'today':
        start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        break;
      case 'week':
        start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6);
        end = today;
        break;
      case 'lastMonth': {
        const firstCurrent = new Date(today.getFullYear(), today.getMonth(), 1);
        start = new Date(firstCurrent.getFullYear(), firstCurrent.getMonth() - 1, 1);
        end = new Date(firstCurrent.getFullYear(), firstCurrent.getMonth(), 0); // last day of previous month
        break;
      }
      case 'year':
        start = new Date(today.getFullYear(), 0, 1);
        end = today;
        break;
      case 'month':
      default:
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
    }

    const toIsoDate = (d: Date) => d.toISOString().slice(0, 10);
    return { start: toIsoDate(start), end: toIsoDate(end) };
  }

  private normalizeDate(input: any): string {
    if (Array.isArray(input) && input.length >= 3) {
      const [y, m, d] = input;
      const pad = (n: number) => n.toString().padStart(2, '0');
      return `${y}-${pad(m)}-${pad(d)}`;
    }
    if (typeof input === 'string') {
      return input.slice(0, 10);
    }
    return '';
  }

  private timelineToMap(timeline: any[] | undefined): Map<string, number> {
    const map = new Map<string, number>();
    (timeline || []).forEach((p: any) => {
      const key = this.normalizeDate(p?.date);
      if (key) {
        map.set(key, p?.amount ?? 0);
      }
    });
    return map;
  }

  private loadDashboard(): void {
    this.isLoading = true;
    this.discrepancyService.getStatusBasedReports(this.startDate, this.endDate).subscribe({
      next: (res: DiscrepancyStatusDashboardDTO) => {
        // Update cash metrics
        this.pendingCash = res.cash?.pendingAmount || 0;
        this.approvedCash = res.cash?.approvedAmount || 0;
        this.rejectedCash = res.cash?.rejectedAmount || 0;

        // Update stock metrics
        this.pendingStock = res.stock?.pendingValue || 0;
        this.approvedStock = res.stock?.approvedValue || 0;
        this.rejectedStock = res.stock?.rejectedValue || 0;

        // Update combined totals
        this.pendingTotal = res.combined?.pendingTotal || 0;
        this.approvedTotal = res.combined?.approvedTotal || 0;
        this.rejectedTotal = res.combined?.rejectedTotal || 0;
        this.grandTotal = res.combined?.grandTotal || 0;

        // Build timeline chart with union of all dates across timelines
        const pendingCashMap = this.timelineToMap(res.cash?.pendingTimeline);
        const approvedCashMap = this.timelineToMap(res.cash?.approvedTimeline);
        const rejectedCashMap = this.timelineToMap(res.cash?.rejectedTimeline);

        const pendingStockMap = this.timelineToMap(res.stock?.pendingTimeline);
        const approvedStockMap = this.timelineToMap(res.stock?.approvedTimeline);
        const rejectedStockMap = this.timelineToMap(res.stock?.rejectedTimeline);

        const labelSet = new Set<string>([...pendingCashMap.keys(), ...approvedCashMap.keys(), ...rejectedCashMap.keys(), ...pendingStockMap.keys(), ...approvedStockMap.keys(), ...rejectedStockMap.keys()]);
        const labels = Array.from(labelSet).sort();

        const pendingDaily = labels.map(date => (pendingCashMap.get(date) || 0) + (pendingStockMap.get(date) || 0));
        const approvedDaily = labels.map(date => (approvedCashMap.get(date) || 0) + (approvedStockMap.get(date) || 0));

        this.statusTimelineChartData = {
          labels,
          datasets: [
            {
              data: pendingDaily,
              label: 'En Attente',
              borderColor: 'rgb(234, 179, 8)',
              backgroundColor: 'rgba(234, 179, 8, 0.1)',
              pointBackgroundColor: 'rgb(234, 179, 8)',
              pointBorderColor: 'rgb(234, 179, 8)',
              fill: true,
              tension: 0.4
            },
            {
              data: approvedDaily,
              label: 'Approuvé',
              borderColor: 'rgb(34, 197, 94)',
              backgroundColor: 'rgba(34, 197, 94, 0.1)',
              pointBackgroundColor: 'rgb(34, 197, 94)',
              pointBorderColor: 'rgb(34, 197, 94)',
              fill: true,
              tension: 0.4
            }
          ]
        };

        // Status breakdown chart
        this.statusBreakdownChartData = {
          labels: ['En Attente', 'Approuvé'],
          datasets: [
            {
              label: 'Caisse',
              backgroundColor: 'rgba(59, 130, 246, 0.8)',
              data: [this.pendingCash, this.approvedCash]
            },
            {
              label: 'Stock',
              backgroundColor: 'rgba(234, 179, 8, 0.8)',
              data: [this.pendingStock, this.approvedStock]
            }
          ]
        };

        // Build separate cash evolution (with total line: pending + approved)
        const cashLabels = Array.from(new Set<string>([...pendingCashMap.keys(), ...approvedCashMap.keys()])).sort();
        const cashPendingDaily = cashLabels.map(d => pendingCashMap.get(d) || 0);
        const cashApprovedDaily = cashLabels.map(d => approvedCashMap.get(d) || 0);
        const cashTotalDaily = cashLabels.map((_, i) => cashPendingDaily[i] + cashApprovedDaily[i]);
        this.cashEvolutionData = {
          labels: cashLabels,
          datasets: [
            { data: cashPendingDaily, label: 'Caisse - En Attente', borderColor: 'rgb(234, 179, 8)', backgroundColor: 'rgba(234, 179, 8, 0.1)', pointBackgroundColor: 'rgb(234, 179, 8)', pointBorderColor: 'rgb(234, 179, 8)', fill: true, tension: 0.3 },
            { data: cashApprovedDaily, label: 'Caisse - Approuvé', borderColor: 'rgb(34, 197, 94)', backgroundColor: 'rgba(34, 197, 94, 0.1)', pointBackgroundColor: 'rgb(34, 197, 94)', pointBorderColor: 'rgb(34, 197, 94)', fill: true, tension: 0.3 },
            { data: cashTotalDaily, label: 'Caisse - Total', borderColor: 'rgb(59, 130, 246)', backgroundColor: 'rgba(59, 130, 246, 0.1)', pointBackgroundColor: 'rgb(59, 130, 246)', pointBorderColor: 'rgb(59, 130, 246)', fill: false, tension: 0.3 }
          ]
        };

        // Build separate stock evolution (with total line: pending + approved)
        const stockLabels = Array.from(new Set<string>([...pendingStockMap.keys(), ...approvedStockMap.keys()])).sort();
        const stockPendingDaily = stockLabels.map(d => pendingStockMap.get(d) || 0);
        const stockApprovedDaily = stockLabels.map(d => approvedStockMap.get(d) || 0);
        const stockTotalDaily = stockLabels.map((_, i) => stockPendingDaily[i] + stockApprovedDaily[i]);
        this.stockEvolutionData = {
          labels: stockLabels,
          datasets: [
            { data: stockPendingDaily, label: 'Stock - En Attente', borderColor: 'rgb(234, 179, 8)', backgroundColor: 'rgba(234, 179, 8, 0.1)', pointBackgroundColor: 'rgb(234, 179, 8)', pointBorderColor: 'rgb(234, 179, 8)', fill: true, tension: 0.3 },
            { data: stockApprovedDaily, label: 'Stock - Approuvé', borderColor: 'rgb(34, 197, 94)', backgroundColor: 'rgba(34, 197, 94, 0.1)', pointBackgroundColor: 'rgb(34, 197, 94)', pointBorderColor: 'rgb(34, 197, 94)', fill: true, tension: 0.3 },
            { data: stockTotalDaily, label: 'Stock - Total', borderColor: 'rgb(59, 130, 246)', backgroundColor: 'rgba(59, 130, 246, 0.1)', pointBackgroundColor: 'rgb(59, 130, 246)', pointBorderColor: 'rgb(59, 130, 246)', fill: false, tension: 0.3 }
          ]
        };

        // Combined evolution with grand total (pending + approved)
        const combinedGrandDaily = labels.map((_, i) => approvedDaily[i] + pendingDaily[i]);
        this.combinedEvolutionData = {
          labels,
          datasets: [
            { data: pendingDaily, label: 'Global - En Attente', borderColor: 'rgb(234, 179, 8)', backgroundColor: 'rgba(234, 179, 8, 0.1)', pointBackgroundColor: 'rgb(234, 179, 8)', pointBorderColor: 'rgb(234, 179, 8)', fill: true, tension: 0.3 },
            { data: approvedDaily, label: 'Global - Approuvé', borderColor: 'rgb(34, 197, 94)', backgroundColor: 'rgba(34, 197, 94, 0.1)', pointBackgroundColor: 'rgb(34, 197, 94)', pointBorderColor: 'rgb(34, 197, 94)', fill: true, tension: 0.3 },
            { data: combinedGrandDaily, label: 'Global - Grand Total', borderColor: 'rgb(59, 130, 246)', backgroundColor: 'rgba(59, 130, 246, 0.1)', pointBackgroundColor: 'rgb(59, 130, 246)', pointBorderColor: 'rgb(59, 130, 246)', fill: false, tension: 0.3 }
          ]
        };

        // Load top 10 products by most negative discrepancy quantity for the selected range
        this.discrepancyService.getTopProductsByDiscrepancyQuantity(this.startDate, this.endDate, 10).subscribe({
          next: (products: ProductDiscrepancyDTO[]) => {
            console.log('Top products received:', products);
            this.topProducts = products;
            this.updateProductDiscrepancyChart();
            this.isLoading = false;
          },
          error: (err) => {
            console.error('Error loading top products:', err);
            this.topProducts = [];
            this.productDiscrepancyData = { labels: [], datasets: [{ label: 'Top 10 - Pertes de Stock', data: [], backgroundColor: 'rgba(99, 102, 241, 0.8)' }] };
            this.isLoading = false;
          }
        });
      },
      error: (err) => {
        console.error('Error loading status-based dashboard:', err);
        this.isLoading = false;
        // Reset to zeros on error
        this.pendingCash = this.approvedCash = this.rejectedCash = 0;
        this.pendingStock = this.approvedStock = this.rejectedStock = 0;
        this.pendingTotal = this.approvedTotal = this.rejectedTotal = this.grandTotal = 0;
        this.statusTimelineChartData = {
          labels: [],
          datasets: [
            { data: [], label: 'En Attente', borderColor: 'rgb(234, 179, 8)', backgroundColor: 'rgba(234, 179, 8, 0.1)', pointBackgroundColor: 'rgb(234, 179, 8)', pointBorderColor: 'rgb(234, 179, 8)', fill: true, tension: 0.4 },
            { data: [], label: 'Approuvé', borderColor: 'rgb(34, 197, 94)', backgroundColor: 'rgba(34, 197, 94, 0.1)', pointBackgroundColor: 'rgb(34, 197, 94)', pointBorderColor: 'rgb(34, 197, 94)', fill: true, tension: 0.4 }
          ]
        };
        this.statusBreakdownChartData = {
          labels: ['En Attente', 'Approuvé'],
          datasets: [
            { label: 'Caisse', data: [0, 0], backgroundColor: 'rgba(59, 130, 246, 0.8)' },
            { label: 'Stock', data: [0, 0], backgroundColor: 'rgba(234, 179, 8, 0.8)' }
          ]
        };
      }
    });
  }

  private updateProductDiscrepancyChart(): void {
    const labels = this.topProducts.map(p => p.productName);
    // Display net negative values as positive numbers (Math.abs)
    const data = this.topProducts.map(p => Math.abs(Number(p.discrepancyValue ?? 0)));
    
    console.log('Chart labels:', labels);
    console.log('Chart data:', data);
    console.log('Raw products:', this.topProducts);

    this.productDiscrepancyData = {
      labels,
      datasets: [
        {
          label: 'Écart Net (unités)',
          data,
          backgroundColor: 'rgba(239, 68, 68, 0.8)' // red for negative/missing
        }
      ]
    };
  }
}